<script setup lang="ts">
  import { ref, watch } from 'vue';
  import {
    decimalFromStored,
    formatPtBrDecimal,
    maskOptionsFrom,
    parsePtBrDecimal,
    scaleCanonical,
    type CompiledFieldNode,
    type FieldValue,
  } from '@record-fill';
  import { useNumberInput } from '../../../model/useNumberInput';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const control = props.node.control.kind === 'number' ? props.node.control : null;
  const mask = maskOptionsFrom({
    numberType: control?.numberType,
    decimalPlaces: control?.decimalPlaces,
    minValue: control?.min,
    maxValue: control?.max,
  });

  function displayText(value: FieldValue): string {
    const canonical = decimalFromStored(value);
    return canonical === null ? '' : formatPtBrDecimal(scaleCanonical(canonical, mask.scale));
  }

  const text = ref(displayText(props.node.value));

  // The answer only changes when the person types, so a text still being typed ("1," or "-") is
  // kept while it stands for the stored answer; a reset by a rule or by the server replaces it.
  watch(
    () => props.node.value,
    (value) => {
      if (parsePtBrDecimal(text.value) === decimalFromStored(value)) return;
      text.value = displayText(value);
    }
  );

  const { handleInput, handleKeydown, handlePaste, handleBlur } = useNumberInput(() => ({ mask }));

  function update(value: string): void {
    text.value = value;
    emit('update', parsePtBrDecimal(value));
  }
</script>

<template>
  <input
    type="text"
    inputmode="decimal"
    :class="[
      inputClass,
      { 'cursor-default': readOnly },
      'w-full min-w-0 text-[11px] leading-4 [font-family:var(--font-base)] placeholder:text-[10px] placeholder:opacity-60 disabled:cursor-not-allowed disabled:bg-[var(--color-gray-09)] disabled:opacity-60',
    ]"
    :value="text"
    :placeholder="control?.placeholder"
    :disabled="disabled"
    :readonly="readOnly"
    :tabindex="readOnly ? -1 : undefined"
    :data-field-id="node.id"
    @keydown="handleKeydown($event, update)"
    @paste="handlePaste($event, update)"
    @input="handleInput($event, update)"
    @blur="handleBlur($event, update)"
  >
</template>
